import {Component, Input, OnInit, Optional} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import { SubscribedComponent } from '../../subscribed.component';
import { SubmissionService } from '../../shared/submission.service';
import { AuthService } from '../../shared/auth.service';
import { NotifierService } from '../../shared/notifier.service';
import { CvService } from '../../cv/cv.service';
import { TailorService } from '../../cv/tailor.service';
import { CvDocumentsHubDialogComponent } from '../../cv/cv-documents-hub/cv-documents-hub-dialog.component';
import { Globals } from '../../shared/Globals';
import {
  Submission,
  SubmissionStatus,
  SubmissionLossReason,
  SUBMISSION_PIPELINE,
  SUBMISSION_LOSS_REASONS,
} from '../../interfaces/submission';
import { GeneratedCv } from '../../interfaces/cv';
import { PackageListItem } from '../../interfaces/application-package';
import { CrmActivity } from '../../interfaces/opportunity';
import { TranslateService } from '@ngx-translate/core';
import { DealContact, OrganizationCrmService } from '../../shared/organization-crm.service';
import { opportunityDescription } from '../opportunity-description';

@Component({
  selector: 'bst-submission-detail',
  templateUrl: './submission-detail.component.html',
  styleUrls: ['./submission-detail.component.scss'],
  standalone: false
})
export class SubmissionDetailComponent extends SubscribedComponent implements OnInit {

  @Input() id: number;
  @Input() embedded = false;

  submission: Submission | null = null;
  activities: CrmActivity[] = [];
  contacts: DealContact[] = [];
  errorMessage: string | null = null;
  /** True when reached from the submissions board (kanban), so a way back there is offered. */
  fromBoard = false;
  descExpanded = false;
  readonly descriptionOf = opportunityDescription;

  // Editable working copies — kept off the loaded model so writeValue-style
  // re-binds on reload don't silently fire change events. Plain ngModel +
  // explicit Save buttons; we never auto-submit on load.
  motivation = '';
  rateOffered: number | null = null;
  selectedStatus: SubmissionStatus = 'draft';
  // null until the user explicitly picks one — a default would silently
  // persist 'unknown' on the first Lost save.
  lossReason: SubmissionLossReason | null = null;
  lossNote = '';
  interviewAt: Date | null = null;

  // CV picker
  cvOptions: GeneratedCv[] = [];
  selectedCvId: number | null = null;

  // Cover letter (package) picker
  packageOptions: PackageListItem[] = [];
  selectedPackageId: number | null = null;

  readonly pipeline = SUBMISSION_PIPELINE;
  readonly lossReasons = SUBMISSION_LOSS_REASONS;

  saving = false;
  savingActivity = false;

  readonly Globals = Globals;

  constructor(@Optional() private dialogRef: NbDialogRef<any>,
              private route: ActivatedRoute,
              private router: Router,
              private submissionService: SubmissionService,
              private auth: AuthService,
              private cvService: CvService,
              private tailorService: TailorService,
              private dialogService: NbDialogService,
              private notifier: NotifierService,
              private translate: TranslateService,
              private organizationCrm: OrganizationCrmService) {
    super();
  }

  get canWrite(): boolean {
    return (this.auth.isAdmin || this.auth.isStaffer) && !!this.auth.user.getValue()?.crm_enabled;
  }

  /**
   * Package generation is admin-only server-side (ApplicationPackagePolicy::
   * create, which also admits superusers) — offering the button to a staffer
   * would 403 after they complete the whole intake.
   */
  get canGenerate(): boolean {
    return (this.auth.isAdmin || this.auth.isSuperuser) && !!this.auth.user.getValue()?.crm_enabled;
  }

  ngOnInit(): void {
    super.ngOnInit();
    const id = this.id || Number(this.route.snapshot.paramMap.get('id'));
    this.fromBoard = this.route.snapshot.queryParamMap.get('from') === 'board';
    if (!id) {
      this.errorMessage = this.translate.instant('crm.err.invalid_submission');
      return;
    }
    void this.load(id);
  }

  backToBoard(): void {
    void this.router.navigate(['submissions']);
  }

  get isLost(): boolean { return this.selectedStatus === 'lost'; }
  get isInterviewScheduled(): boolean { return this.selectedStatus === 'interview_scheduled'; }
  get isWon(): boolean { return this.submission?.status === 'won'; }
  get isConverted(): boolean { return !!this.submission?.converted_Project_id; }
  get canRemove(): boolean {
    return !!this.submission && this.submission.status !== 'won' && !this.submission.converted_Project_id;
  }

  /** True when the submission has a CV ready (attached GeneratedCv or package with cv draft/finalized). */
  get cvReady(): boolean {
    const s = this.submission;
    if (!s) { return false; }
    return !!s.GeneratedCv_id ||
      ['draft', 'finalized'].includes(s.application_package?.cv_status ?? '');
  }

  /** True when the submission has a cover letter ready (package with letter draft/finalized). */
  get letterReady(): boolean {
    const s = this.submission;
    if (!s) { return false; }
    return ['draft', 'finalized'].includes(s.application_package?.letter_status ?? '');
  }

  /** True when both CV and letter are ready. */
  get docsComplete(): boolean { return this.cvReady && this.letterReady; }

  consultantName(): string {
    const s = this.submission;
    if (!s) { return ''; }
    if (!s.consultant) { return `#${s.consultant_User_id}`; }
    return `${s.consultant.name_first} ${s.consultant.name_last}`.trim();
  }

  cvLabel(cv: GeneratedCv): string {
    const date = cv.created_at ? cv.created_at.substring(0, 10) : '';
    return `#${cv.id} · ${cv.template_id} · ${cv.locale}${date ? ' · ' + date : ''}`;
  }

  packageLabel(pkg: PackageListItem): string {
    const date = pkg.created_at ? pkg.created_at.substring(0, 10) : '';
    const subject = pkg.vacancy_title ?? this.translate.instant('crm.manual_deal');
    return `#${pkg.id} · ${subject}${date ? ' · ' + date : ''}`;
  }

  back(): void {
    if (this.embedded && this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    const oppId = this.submission?.Opportunity_id;
    if (oppId) {
      void this.router.navigate(['opportunities', oppId]);
    } else {
      void this.router.navigate(['opportunities', 'view', 'open']);
    }
  }

  async addActivity(e: { type: string; body: string; Contact_id?: number | null; occurred_at?: string }): Promise<void> {
    this.savingActivity = true;
    try {
      const res = await this.submissionService.logActivity(this.submission!.id, e.type, e.body, e.Contact_id, e.occurred_at);
      this.activities.unshift(res.data);
    } catch (err) {
      console.error('log activity failed', err);
    } finally {
      this.savingActivity = false;
    }
  }

  async editActivity(e: { id: number; body: string; occurred_at?: string }): Promise<void> {
    try {
      const updated = await this.organizationCrm.updateActivity(e.id, { body: e.body, occurred_at: e.occurred_at });
      if (!updated) { return; }
      const idx = this.activities.findIndex(a => a.id === e.id);
      if (idx !== -1) { this.activities[idx] = { ...this.activities[idx], body: updated.body, occurred_at: updated.occurred_at }; }
    } catch (err) {
      console.error('edit activity failed', err);
    }
  }

  async deleteActivity(id: number): Promise<void> {
    const deleted = await this.organizationCrm.deleteActivity(id).catch(() => false);
    if (deleted) { this.activities = this.activities.filter(a => a.id !== id); }
  }

  async saveMotivation(): Promise<void> {
    if (!this.submission || this.motivation === (this.submission.motivation_text ?? '')) { return; }
    await this.run(async () => {
      const updated = await this.submissionService.setMotivation(this.submission!.id, this.motivation);
      this.applyLoaded(updated);
    }, this.translate.instant('crm.toast.motivation_saved'), this.translate.instant('crm.err.motivation'));
  }

  async saveRate(): Promise<void> {
    if (!this.submission || this.rateOffered === (this.submission.rate_offered ?? null)) { return; }
    await this.run(async () => {
      const updated = await this.submissionService.setRate(this.submission!.id, this.rateOffered);
      this.applyLoaded(updated);
    }, this.translate.instant('crm.toast.rate_saved'), this.translate.instant('crm.err.rate'));
  }

  async saveCv(): Promise<void> {
    if (!this.submission || !this.selectedCvId || this.selectedCvId === this.submission.GeneratedCv_id) { return; }
    await this.run(async () => {
      const updated = await this.submissionService.attachCv(this.submission!.id, this.selectedCvId!);
      this.applyLoaded(updated);
    }, this.translate.instant('crm.toast.cv_attached'), this.translate.instant('crm.err.attach_cv'));
  }

  async saveLetterPackage(): Promise<void> {
    if (!this.submission || !this.selectedPackageId
      || this.selectedPackageId === this.submission.application_package?.id) { return; }
    await this.run(async () => {
      const updated = await this.submissionService.attachPackage(this.submission!.id, this.selectedPackageId!);
      this.applyLoaded(updated);
    }, this.translate.instant('crm.toast.letter_attached'), this.translate.instant('crm.err.attach_letter'));
  }

  /**
   * Open the documents hub to generate a CV/letter for this submission's
   * consultant. The submissionId is forwarded through the hub → tailor
   * queryParam, so the tailor create POST includes submission_id and the
   * backend auto-links the resulting package to this submission (#767).
   */
  generate(): void {
    if (!this.submission) { return; }
    // Close this detail whenever it is a dialog so the hub never stacks over
    // it (#503, mirrors market-vacancy-detail.component.ts's createDocuments()).
    if (this.dialogRef) { this.dialogRef.close(); }
    this.dialogService.open(CvDocumentsHubDialogComponent, {
      context: {
        vacancyId:    this.submission.opportunity?.MarketVacancy_id ?? null,
        userId:       this.submission.consultant_User_id,
        submissionId: this.submission.id,
      },
      closeOnBackdropClick: false,
      closeOnEsc: true,
      hasBackdrop: true,
    });
  }

  /** Open the attached GeneratedCv (existing CV) in a new tab. */
  openCv(): void {
    const cvId = this.submission?.cv?.id ?? this.submission?.GeneratedCv_id;
    if (!cvId) { return; }
    window.open(this.cvService.previewUrl(cvId), '_blank');
  }

  /** Open the package CV PDF in a new tab. */
  openPackageCv(): void {
    const pkg = this.submission?.application_package;
    if (!pkg?.id || !pkg.cv_pdf_gcs_key) { return; }
    window.open(this.tailorService.downloadUrl(pkg.id, 'cv'), '_blank');
  }

  /** Open the package cover letter PDF in a new tab. */
  openLetter(): void {
    const pkg = this.submission?.application_package;
    if (!pkg?.id || !pkg.letter_pdf_gcs_key) { return; }
    window.open(this.tailorService.downloadUrl(pkg.id, 'letter'), '_blank');
  }

  async saveStatus(): Promise<void> {
    if (!this.submission) { return; }

    const isCurrentlyLost = this.selectedStatus === 'lost';
    // Lost needs an explicit reason: hold the save until one is picked (the
    // reason select sits next to the status select) instead of sending a
    // default the server would either 422 or silently record.
    if (isCurrentlyLost && !this.lossReason) { return; }
    const reason = isCurrentlyLost ? this.lossReason : undefined;
    const note = isCurrentlyLost ? (this.lossNote.trim() || undefined) : undefined;
    const isInterview = this.selectedStatus === 'interview_scheduled';
    const interviewAt = isInterview && this.interviewAt ? Globals.toISODate(this.interviewAt) : undefined;
    const loadedInterviewAt = this.submission.interview_at ? Globals.toISODate(this.submission.interview_at) : undefined;

    // Check if anything actually changed
    const changed = this.selectedStatus !== this.submission.status ||
                    (isCurrentlyLost && reason !== this.submission.loss_reason) ||
                    (isCurrentlyLost && note !== (this.submission.loss_reason_note ?? undefined)) ||
                    (isInterview && interviewAt !== undefined && interviewAt !== loadedInterviewAt);

    if (!changed) { return; }

    await this.run(async () => {
      const updated = await this.submissionService.setStatus(this.submission!.id, this.selectedStatus, reason, note, interviewAt);
      this.applyLoaded(updated as Submission);
    }, this.translate.instant('crm.toast.status_updated'), this.translate.instant('crm.err.status_failed'));
  }

  async convert(): Promise<void> {
    if (!this.submission) { return; }
    const consultantUserId = this.submission.consultant_User_id;
    await this.run(async () => {
      const project: any = await this.submissionService.convertToProject(this.submission!.id);
      // Toast before navigation — the route change tears this component down.
      void this.notifier.notify(this.translate.instant('crm.toast.converted'), null, 'success');
      const projectId = project?.id;
      if (projectId) {
        const queryParams: Record<string, any> = { edit: 1 };
        if (consultantUserId) { queryParams['assign'] = consultantUserId; }
        await this.router.navigate(['projects', projectId], { queryParams });
      }
    }, null, this.translate.instant('crm.err.convert'));
  }

  /** Confirm then delete the submission; navigate back to the opportunity. */
  async removeSubmission(): Promise<void> {
    if (!this.submission) { return; }
    if (!confirm(this.translate.instant('crm.remove_confirm'))) { return; }
    const oppId = this.submission.Opportunity_id;
    this.saving = true;
    try {
      const res: any = await this.submissionService.deleteSubmission(this.submission.id);
      if (res && res.deleted) {
        if (this.embedded && this.dialogRef) {
          this.dialogRef.close({ deleted: true });
        } else if (oppId) {
          void this.router.navigate(['opportunities', oppId]);
        } else {
          void this.router.navigate(['opportunities', 'view', 'open']);
        }
      }
    } catch (e) {
      console.error('submission delete failed', e);
    } finally {
      this.saving = false;
    }
  }

  /**
   * Run an action with shared saving/error state. On success, fire an optional
   * success toast; on failure, set the error banner AND fire an error toast.
   * convert() passes a null success message since it toasts before navigating.
   */
  private async run(
    work: () => Promise<void>,
    successMessage: string | null = null,
    errorMessage: string = this.translate.instant('crm.err.action_failed'),
  ): Promise<void> {
    this.saving = true;
    this.errorMessage = null;
    try {
      await work();
      if (successMessage) {
        void this.notifier.notify(successMessage, null, 'success');
      }
    } catch (e) {
      console.error('submission detail action failed', e);
      this.errorMessage = errorMessage;
      void this.notifier.notify(errorMessage, null, 'danger');
    } finally {
      this.saving = false;
    }
  }

  private async load(id: number): Promise<void> {
    this.loading.next(true);
    this.errorMessage = null;
    try {
      const sub = await this.submissionService.show(id);
      this.applyLoaded(sub);
      await this.loadCvOptions();
      await this.loadPackageOptions();

      // Activities are non-fatal: timeline degrades gracefully to empty state.
      const acts = await this.submissionService.activities(id).catch((e) => {
        console.error('submission activities load failed', e);
        return null;
      });
      this.activities = acts?.data ?? [];

      // Contacts (for the cover_letter picker) are non-fatal too, same as
      // opportunity-detail's pattern, and offer both counterparties for the
      // same reason: a brokered deal's letter is often addressed to the broker.
      if (sub.opportunity) {
        this.contacts = await this.organizationCrm.listDealContacts(
          {id: sub.opportunity.for_Client_id, name: sub.opportunity.end_client?.name},
          {id: sub.opportunity.via_Client_id, name: sub.opportunity.broker?.name},
        );
      }
    } catch (e) {
      console.error('submission detail load failed', e);
      this.errorMessage = this.translate.instant('crm.err.load_submission');
    } finally {
      this.loading.next(false);
    }
  }

  // Set the editable working copies silently from a freshly loaded model.
  private applyLoaded(sub: Submission): void {
    this.submission = sub;
    this.motivation = sub.motivation_text ?? '';
    this.rateOffered = sub.rate_offered ?? null;
    this.selectedStatus = sub.status;
    this.selectedCvId = sub.GeneratedCv_id ?? null;
    this.selectedPackageId = sub.application_package?.id ?? null;
    // Always mirror the server value (including null) so a cleared reason
    // can't be silently re-submitted from stale local state.
    this.lossReason = (sub.loss_reason as SubmissionLossReason) ?? null;
    this.lossNote = sub.loss_reason_note ?? '';
    this.interviewAt = sub.interview_at ? Globals.fromDateString(sub.interview_at) : null;
  }

  private async loadCvOptions(): Promise<void> {
    const consultantId = this.submission?.consultant_User_id;
    if (!consultantId) { return; }
    try {
      const page = await this.cvService.listForUser(consultantId).toPromise();
      this.cvOptions = page?.data ?? [];
    } catch (e) {
      // Non-fatal: the picker degrades to "no CVs available"; the rest of
      // the detail view stays usable.
      console.error('submission CV options load failed', e);
      this.cvOptions = [];
    }
  }

  private async loadPackageOptions(): Promise<void> {
    const consultantId = this.submission?.consultant_User_id;
    if (!consultantId) { return; }
    try {
      this.packageOptions = await this.tailorService.listForUser(consultantId).toPromise();
    } catch (e) {
      // Non-fatal: the picker degrades to "no packages available"; the rest
      // of the detail view stays usable.
      console.error('submission package options load failed', e);
      this.packageOptions = [];
    }
  }
}
